<template>
  <modal
    ref="modal"
    :data-url="dataUrl"
    modal-title="Statement of costs"
    modal-class="statement-of-costs-modal"
    @data-loaded="modalDataLoaded"
  >
    <template #modal-content>
      <table class="table table-sm table-bordered resources-table">
        <thead class="table-light">
          <tr>
            <th class="tight">#</th>
            <th class="tight">Cost type</th>
            <th class="tight">Transport mode</th>
            <th>From</th>
            <th>To</th>
            <th class="tight">Departure date/time</th>
            <th class="tight">Arrival date/time</th>
            <th class="amount">Amount</th>
            <th class="amount">Invoice/evidence</th>
          </tr>
        </thead>
        <tbody>
          <template
            v-for="(item, itemIdx) of items"
            :key="`item-row-${itemIdx}`"
          >
            <tr
              :class="{ 'for-destruction table-danger': item._destroy }"
            >
              <td class="tight">{{ itemIdx + 1 }}.</td>
              <td class="tight">{{ fCostType(item) }}</td>
              <td class="tight">{{ fTransportMode(item) }}</td>
              <td>{{ fJourneyFrom(item) }}</td>
              <td>{{ fJourneyTo(item) }}</td>
              <td class="tight">{{ fDateTime(fDepartureDate(item)) }}</td>
              <td class="tight">{{ fDateTime(fArrivalDate(item)) }}</td>
              <td
                class="amount"
                :rowspan="itemRowspan(item)"
              >
                {{ fAmount(item) }}
              </td>
              <td :rowspan="itemRowspan(item)">
                <ul
                  v-if="Array.isArray(item.files) && item.files.length > 0"
                  class="files-list m-0"
                >
                  <li
                    v-for="file of item.files"
                    :key="`item-row-file-${itemIdx}-${file.id}`"
                    class="text-truncate"
                  >
                    <a
                      :href="file.links.download"
                      target="_blank"
                    >
                      {{ file.filename }}
                    </a>
                  </li>
                </ul>
              </td>
            </tr>
            <tr
              v-if="isRoundTripTravel(item)"
              :key="`item-roundtrip-row-${itemIdx}`"
            >
              <td />
              <td />
              <td class="text-end text-secondary"><span class="glyphicons return-trip" /></td>
              <td>{{ item.inboundFrom }}</td>
              <td>{{ item.inboundTo }}</td>
              <td>{{ fDateTime(item.inboundDeparture) }}</td>
              <td>{{ fDateTime(item.inboundArrival) }}</td>
            </tr>
            <tr
              v-if="hasCarClaimDetails(item)"
              :key="`item-car-claim-details-row-${itemIdx}`"
            >
              <td />
              <td />
              <td />
              <td colspan="6">
                &#x2713; {{ item.privateCarClaimDetails }}
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </template>
  </modal>
</template>

<script setup lang="ts">
import {nextTick, ref, useTemplateRef} from "vue"
import Jsona from "jsona"

import Modal from "./modal.vue"

import {isNonEmptyString, isPresent} from "../js-utils"

type JsonaBody = Parameters<Jsona["deserialize"]>[0]

enum CostType {
  transport = 1,
  accommodation = 2,
  meals = 3,
}

enum TransportMode {
  plane = 1,
  train = 2,
  bus = 3,
  ship = 4,
  privateCar = 5,
  other = 6,
}

const COST_TYPES = [
  {id: null, title: ''},
  {id: CostType.transport, title: 'Transport', extendedTitle: 'Transport'},
  {id: CostType.accommodation, title: 'Accommodation', extendedTitle: 'Accommodation (national participants only)'},
  {id: CostType.meals, title: 'Meals', extendedTitle: 'Meals (national participants only)'},
]

const TRANSPORT_MODES = [
  {id: null, title: ''},
  {id: TransportMode.plane, title: 'Plane'},
  {id: TransportMode.train, title: 'Train'},
  {id: TransportMode.bus, title: 'Bus'},
  {id: TransportMode.ship, title: 'Ship'},
  {id: TransportMode.privateCar, title: 'Private car'},
  {id: TransportMode.other, title: 'Other'},
]

enum TripType {
  oneWay = 1,
  roundTrip = 2,
}

interface CostsItemFile {
  id: string;
  filename: string;
  links: {
    download: string;
  };
}

interface DateTimeItem {
  date: string;
  time?: string;
}

interface CostsItem {
  costTypeId: CostType;
  transportModeId: TransportMode | null;
  tripTypeId: TripType | null;

  privateCarDistance: string | number | null;
  amount: string | number;
  privateCarAmount: string | number | null;
  from: string;
  to: string;
  arrival: DateTimeItem;
  departure: DateTimeItem;
  _destroy: boolean;
  files?: CostsItemFile[];
  inboundFrom: string;
  inboundTo: string;
  inboundDeparture: DateTimeItem;
  inboundArrival: DateTimeItem;
  privateCarClaimDetails: string;
}

const modal = useTemplateRef<InstanceType<typeof Modal>>("modal")
const dataUrl = ref<string | undefined>()
const items = ref<CostsItem[]>([])

const showModal = (url: string): void => {
  dataUrl.value = url
  nextTick(() => {
    modal.value?.showModal()
  })
}

const modalDataLoaded = (status: boolean, data?: unknown): void => {
  if (status && data != null) {
    const jsdata = new Jsona().deserialize(data as JsonaBody)
    if (Array.isArray(jsdata)) {
      items.value = jsdata as CostsItem[]
    }
  } else {
    items.value = []
  }
}

const isRoundTripTravel = (item: CostsItem): boolean => {
  return item && item.costTypeId === CostType.transport &&
    item.tripTypeId === TripType.roundTrip
}

const hasCarClaimDetails = (item: CostsItem): boolean => {
  return isPresent(item.privateCarClaimDetails)
}

const itemRowspan = (item: CostsItem): number | undefined => {
  if (isRoundTripTravel(item)) {
    return 2
  }
  return undefined
}

const fCostType = (item: CostsItem): string => {
  return item.costTypeId ? COST_TYPES[item.costTypeId].title : ''
}

const fTransportMode = (item: CostsItem): string => {
  if (item.costTypeId == CostType.transport && item.transportModeId) {
    if (item.transportModeId == TransportMode.privateCar) {
      return `${TRANSPORT_MODES[item.transportModeId].title} (${item.privateCarDistance}km)`
    }
    return TRANSPORT_MODES[item.transportModeId].title
  } else {
    return ''
  }
}

const fJourneyFrom = (item: CostsItem): string => {
  if (item.costTypeId === CostType.transport) {
    return item.from
  }
  return ''
}

const fJourneyTo = (item: CostsItem): string => {
  if (item.costTypeId === CostType.transport) {
    return item.to
  }
  return ''
}

const fDateTime = (item: DateTimeItem | null): string => {
  const parts = []
  if (item) {
    if (isNonEmptyString(item.date)) {
      parts.push(item.date)
    }
    if (isNonEmptyString(item.time)) {
      parts.push(item.time)
    }
  }

  return parts.length > 0 ? parts.join(' ') : ''
}

const fArrivalDate = (item: CostsItem): DateTimeItem | null => {
  if (item.costTypeId === CostType.transport || item.costTypeId === CostType.accommodation) {
    return item.arrival
  }
  return null
}

const fDepartureDate = (item: CostsItem): DateTimeItem | null => {
  if (item.costTypeId === CostType.transport || item.costTypeId === CostType.accommodation) {
    return item.departure
  }
  return null
}

const fAmount = (item: CostsItem): string => {
  let amount = item.amount as string | number | null
  if (item.costTypeId === CostType.transport && item.transportModeId === TransportMode.privateCar) {
    amount = item.privateCarAmount
  }

  let amountVal = amount
  if (typeof amount === 'string' && amount.length > 0) {
    amountVal = parseFloat(amount)
  }
  if (typeof amountVal === 'number' && !isNaN(amountVal)) {
    return amountVal.toFixed(2)
  }

  if (amount != null) {
    return amount.toString()
  }
  return ''
}

defineExpose({
  showModal,
})
</script>
